// File: frontend/src/pages/KnowledgeGraph/index.tsx
import React from 'react';
import { Box, Snackbar, Alert, Breadcrumbs, Link, Typography, ToggleButtonGroup, ToggleButton, IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HeroHeader from './components/HeroHeader';
import StatsBar from './components/StatsBar';
import EntitySearchBar from './components/EntitySearchBar';
import ExampleQueries from './components/ExampleQueries';
import ConstellationGraph from './components/ConstellationGraph';
import ForceGraph3DView from './components/ForceGraph3DView';
import EntityDetailPanel from './components/EntityDetailPanel';
import { LegendItem } from './components/GraphControls';
import { useConstellation } from './hooks/useConstellation';
import { ENTITY_TYPE_LABELS } from './types';

type GraphMode = 'cytoscape' | '3d';

const KnowledgeGraph: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [errorSnackbar, setErrorSnackbar] = React.useState<string | null>(null);
  const [graphMode, setGraphMode] = React.useState<GraphMode>('cytoscape');
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const graphAreaRef = React.useRef<HTMLDivElement>(null);

  // Listen for fullscreen change events (e.g. user presses Escape)
  React.useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const toggleFullscreen = React.useCallback(async () => {
    if (!graphAreaRef.current) return;
    if (!document.fullscreenElement) {
      await graphAreaRef.current.requestFullscreen();
    } else {
      await document.exitFullscreen();
    }
  }, []);

  const {
    stats,
    statsLoading,
    searchQuery,
    setSearchQuery,
    entityTypeFilter,
    setEntityTypeFilter,
    doSearch,
    searchResults,
    searchLoading,
    hasSearched,
    cluster,
    clusterLoading,
    clusterRootId,
    depth,
    setDepth,
    showInferred,
    setShowInferred,
    truncated,
    totalNodeCount,
    loadCluster,
    history,
    hiddenTypes,
    toggleHiddenType,
    selectedNodeId,
    selectedEntity,
    selectEntity,
    selectNodeById,
    expandNode,
    timeline,
    timelineLoading,
    relatedNodes,
    selectedConnectionCount,
    detailCollapsed,
    setDetailCollapsed,
    handleExampleQuery,
  } = useConstellation();

  // Legend with live per-type counts, derived from the loaded cluster
  const legendItems: LegendItem[] = React.useMemo(() => {
    if (!cluster) return [];
    const counts: Record<string, number> = {};
    cluster.nodes.forEach((n) => {
      counts[n.type] = (counts[n.type] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([type, count]) => ({ type, label: ENTITY_TYPE_LABELS[type] || type, count }));
  }, [cluster]);

  const handleDepthChange = React.useCallback((d: number) => {
    setDepth(d);
    if (clusterRootId) loadCluster(clusterRootId, d);
  }, [setDepth, clusterRootId, loadCluster]);

  const handleShowInferredChange = React.useCallback((v: boolean) => {
    setShowInferred(v);
    if (clusterRootId) loadCluster(clusterRootId, undefined, v);
  }, [setShowInferred, clusterRootId, loadCluster]);

  const handleBreadcrumbClick = React.useCallback((id: string) => {
    selectNodeById(id);
    loadCluster(id);
  }, [selectNodeById, loadCluster]);

  const graphProps = {
    cluster,
    loading: clusterLoading,
    selectedNodeId,
    onSelectNode: selectNodeById,
    onExpandNode: expandNode,
    depth,
    onDepthChange: handleDepthChange,
    showInferred,
    onShowInferredChange: handleShowInferredChange,
    truncated,
    totalNodeCount,
    hiddenTypes,
    onToggleType: toggleHiddenType,
    legendItems,
  };

  return (
    <Box sx={{ maxWidth: '1600px', mx: 'auto' }}>
      {/* Hero */}
      <HeroHeader />

      {/* Stats */}
      <StatsBar stats={stats} loading={statsLoading} />

      {/* Search */}
      <EntitySearchBar
        query={searchQuery}
        onQueryChange={setSearchQuery}
        entityTypeFilter={entityTypeFilter}
        onEntityTypeFilterChange={setEntityTypeFilter}
        onSearch={doSearch}
        results={searchResults}
        loading={searchLoading}
        onSelectEntity={selectEntity}
      />

      {/* Example queries (hidden after first search) */}
      {!hasSearched && <ExampleQueries onSelect={handleExampleQuery} />}

      {/* Toolbar: exploration breadcrumb + view mode + fullscreen */}
      {!isMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" sx={{ opacity: 0.5 }} />}
            sx={{ minHeight: 32, '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap', overflow: 'hidden' } }}
          >
            {history.length > 1 &&
              history.map((entry, i) =>
                i === history.length - 1 ? (
                  <Typography key={entry.id} variant="body2" color="text.primary" noWrap sx={{ maxWidth: 220 }}>
                    {entry.name}
                  </Typography>
                ) : (
                  <Link
                    key={entry.id}
                    component="button"
                    variant="body2"
                    underline="hover"
                    color="text.secondary"
                    onClick={() => handleBreadcrumbClick(entry.id)}
                    noWrap
                    sx={{ maxWidth: 180 }}
                  >
                    {entry.name}
                  </Link>
                ),
              )}
          </Breadcrumbs>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <ToggleButtonGroup
              value={graphMode}
              exclusive
              onChange={(_, val) => { if (val) setGraphMode(val as GraphMode); }}
              size="small"
            >
              <ToggleButton value="cytoscape">
                <ScatterPlotIcon sx={{ mr: 0.5, fontSize: 18 }} />
                2D Graph
              </ToggleButton>
              <ToggleButton value="3d">
                <ViewInArIcon sx={{ mr: 0.5, fontSize: 18 }} />
                3D Graph
              </ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen graph'}>
              <IconButton onClick={toggleFullscreen} size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
                {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      <Box
        ref={graphAreaRef}
        sx={{
          display: 'flex',
          gap: 2,
          height: isMobile ? 'auto' : isFullscreen ? '100vh' : 'calc(100vh - 560px)',
          minHeight: isMobile ? 'auto' : 400,
          ...(isFullscreen && { bgcolor: 'background.default', p: 1 }),
        }}
      >
        {/* Graph area */}
        <Box sx={{ flex: detailCollapsed || isMobile ? 1 : '0 0 70%', position: 'relative' }}>
          {graphMode === 'cytoscape' ? (
            <ConstellationGraph {...graphProps} rootNodeId={clusterRootId} />
          ) : (
            <ForceGraph3DView {...graphProps} />
          )}
        </Box>

        {/* Detail panel */}
        {!isMobile && (
          <Box sx={{ flex: detailCollapsed ? '0 0 20px' : '0 0 30%', position: 'relative', minWidth: detailCollapsed ? 20 : 280 }}>
            <EntityDetailPanel
              entity={selectedEntity}
              timeline={timeline}
              timelineLoading={timelineLoading}
              relatedNodes={relatedNodes}
              connectionCount={selectedConnectionCount}
              collapsed={detailCollapsed}
              onToggleCollapse={() => setDetailCollapsed(!detailCollapsed)}
              onCenterEntity={(id) => loadCluster(id)}
              onSelectRelated={(id) => {
                selectNodeById(id);
                loadCluster(id);
              }}
            />
          </Box>
        )}

        {/* Mobile: show entity details below search results */}
        {isMobile && selectedEntity && (
          <EntityDetailPanel
            entity={selectedEntity}
            timeline={timeline}
            timelineLoading={timelineLoading}
            relatedNodes={relatedNodes}
            connectionCount={selectedConnectionCount}
            collapsed={false}
            onToggleCollapse={() => {}}
            onCenterEntity={() => {}}
            onSelectRelated={(id) => {
              selectNodeById(id);
            }}
          />
        )}
      </Box>

      {/* Error snackbar */}
      <Snackbar
        open={!!errorSnackbar}
        autoHideDuration={6000}
        onClose={() => setErrorSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorSnackbar(null)}>
          {errorSnackbar}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default KnowledgeGraph;
